// src/pages/Security.tsx
/**
 * Owner-only security page: admin second-factor enrolment, active sessions and the emergency levers.
 *
 * <p>This page is deliberately NEVER wrapped in the step-up gate. It is the way IN: an admin who has
 * not verified this session — or has not enrolled at all — has to be able to reach it, or the
 * feature is a lock with the key on the inside. Each card locks its own contents instead, and the
 * locked ones offer the step-up modal rather than a dead end.
 *
 * <p>WHY THE CARDS DO NOT RENDER WHEN THE STATUS COULD NOT BE READ. `StepUpGate` fails OPEN on a
 * status error, because a network blip must never lock the owner out of pages the server is still
 * protecting on its own. This page inverts that, and the asymmetry is deliberate: the first button
 * on the TOTP card starts enrolment, and enrolment REPLACES the live secret the moment it is called.
 * Rendering "Dvojstupňové overenie nie je zapnuté." next to that button on a guess would invite the
 * owner to destroy a working second factor because a status request timed out. So the page shows the
 * error and a retry, and asks nothing of the reader it cannot answer for.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PeerResetCard from '../components/security/PeerResetCard';
import SessionsCard from '../components/security/SessionsCard';
import TotpCard from '../components/security/TotpCard';
import {
    Banner,
    Card,
    Container,
    Header,
    BackButton,
    Heading,
    Hint,
    Kpi,
    KpiLabel,
    KpiValue,
    Kpis,
    Lead,
    Secondary,
} from '../components/security/ui';
import { selectMfaIsCurrent } from '../store/mfaSlice';
import { loadMfaStatus } from '../store/mfaThunks';
import { AppDispatch, RootState } from '../store/store';

const STATUS_LOAD_FAILED = 'Nepodarilo sa overiť stav dvojstupňového overenia.';

const Security: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { user, isAuthenticated, accessToken } = useSelector((state: RootState) => state.auth);
    const mfa = useSelector((state: RootState) => state.mfa);
    const isCurrent = useSelector((state: RootState) => selectMfaIsCurrent(state));

    // Admin gate. The entries that link here are already admin-only, but guard the route itself
    // too: someone could navigate here directly. Same shape as Invoicing and Campaigns.
    //
    // The login response supplies the canonical admin indicator through AuthorizationService
    // — the API is the real authority (hasRole('ADMIN') off c_role) and refuses regardless. This is
    // only about not showing an admin an empty page.
    useEffect(() => {
        if (isAuthenticated && !user?.isAdmin) {
            navigate('/');
        }
    }, [isAuthenticated, user, navigate]);

    // Forced, not cached: this page exists to act on the status and then show the result, so a value
    // left over from whichever admin screen loaded it first is not good enough. `accessToken` is in
    // the deps so signing in again while the page is open re-asks for the new session's answer; the
    // thunk itself dedupes a load already in flight and no-ops for anyone who is not an admin.
    useEffect(() => {
        dispatch(loadMfaStatus({ force: true }));
    }, [dispatch, accessToken]);

    /**
     * The token a status has been READ for at least once, latched.
     *
     * Gating the cards on `loadState === 'ready'` alone was a bug, and an expensive one. Every forced
     * reload — the one after enrolment, the one after regenerating codes, the one the step-up modal
     * fires when a code is accepted — dips `loadState` back to `'loading'`, which unmounted all three
     * cards and remounted them with fresh state. The visible symptoms were a success message that
     * never appeared and a second, pointless fetch of the session list. The invisible one was worse:
     * a reload arriving while the TOTP card is on its scan step would take the QR code and the Base32
     * key off the screen, at the one moment the server has ALREADY replaced the secret and the only
     * copy of the new one is the one being displayed.
     *
     * A same-token refresh keeps the values it is refreshing (`statusLoading` clears them only when
     * the token changed), so there is nothing to hide while one is in flight. This latch drops only
     * when the session itself does.
     */
    const [loadedForToken, setLoadedForToken] = useState<string | null>(null);
    useEffect(() => {
        if (isCurrent && mfa.loadState === 'ready') {
            setLoadedForToken(mfa.forToken);
        }
    }, [isCurrent, mfa.loadState, mfa.forToken]);

    const hasStatus =
        (loadedForToken !== null && loadedForToken === accessToken) ||
        (isCurrent && mfa.loadState === 'ready');
    const refreshFailed = isCurrent && mfa.loadState === 'error';
    /** Only the FIRST read failing hides the page — see the class comment on why this one fails
     *  closed. A later refresh failing leaves the last known-good status on screen with a warning. */
    const firstReadFailed = refreshFailed && !hasStatus;
    const lowCodes =
        mfa.enabled && mfa.recoveryCodesRemaining !== null && mfa.recoveryCodesRemaining <= 2;

    return (
        <Container>
            <Header>
                <BackButton onClick={() => navigate(-1)}>&larr;</BackButton>
                <Heading>Bezpečnosť</Heading>
            </Header>
            <Lead>Dvojstupňové overenie správcovských účtov, aktívne relácie a núdzové úkony.</Lead>

            {!hasStatus && !firstReadFailed && (
                <Card>
                    <Hint style={{ margin: 0 }}>Načítavam stav dvojstupňového overenia…</Hint>
                </Card>
            )}

            {firstReadFailed && (
                <Card>
                    <Banner>{mfa.loadError || STATUS_LOAD_FAILED}</Banner>
                    <Hint style={{ margin: '0 0 14px' }}>
                        Bez tohto údaju nevieme, či je dvojstupňové overenie zapnuté — a nastavenie
                        naslepo by zneplatnilo záznam, ktorý možno funguje. Skúste to znova.
                    </Hint>
                    <Secondary type="button" onClick={() => dispatch(loadMfaStatus({ force: true }))}>
                        Skúsiť znova
                    </Secondary>
                </Card>
            )}

            {hasStatus && (
                <>
                    {refreshFailed && (
                        <Banner $warn>
                            {mfa.loadError || STATUS_LOAD_FAILED} Zobrazujeme naposledy načítaný stav.
                        </Banner>
                    )}

                    <Kpis>
                        <Kpi>
                            <KpiLabel>Dvojstupňové overenie</KpiLabel>
                            <KpiValue $tone={mfa.enabled ? 'ok' : 'warn'}>
                                {mfa.enabled ? 'Zapnuté' : 'Vypnuté'}
                            </KpiValue>
                        </Kpi>
                        <Kpi>
                            <KpiLabel>Táto relácia</KpiLabel>
                            <KpiValue
                                $tone={
                                    mfa.sessionVerified ? 'ok' : mfa.enabled ? 'warn' : 'plain'
                                }
                            >
                                {mfa.sessionVerified ? 'Overená' : 'Neoverená'}
                            </KpiValue>
                        </Kpi>
                        <Kpi>
                            <KpiLabel>Vynucovanie na serveri</KpiLabel>
                            <KpiValue>{mfa.enforced ? 'zapnuté' : 'vypnuté'}</KpiValue>
                        </Kpi>
                        <Kpi>
                            <KpiLabel>Záložné kódy</KpiLabel>
                            {/* "—" and not "0": an older jar answers the two-field status body and
                                simply does not know the count. Zero remaining is a warning worth
                                showing; "we did not ask" is not. */}
                            <KpiValue $tone={lowCodes ? 'warn' : 'plain'}>
                                {mfa.recoveryCodesRemaining === null
                                    ? '—'
                                    : mfa.recoveryCodesRemaining}
                            </KpiValue>
                        </Kpi>
                    </Kpis>

                    <TotpCard
                        enabled={mfa.enabled}
                        sessionVerified={mfa.sessionVerified}
                        recoveryCodesRemaining={mfa.recoveryCodesRemaining}
                    />
                    <SessionsCard enabled={mfa.enabled} sessionVerified={mfa.sessionVerified} />
                    <PeerResetCard enabled={mfa.enabled} sessionVerified={mfa.sessionVerified} />

                    <Hint>
                        {mfa.enforced
                            ? 'Server vyžaduje druhý faktor aj pri ďalších správcovských akciách.'
                            : 'Vynucovanie druhého faktora pri ďalších správcovských akciách je vypnuté. Zapína sa v nastavení servera, až keď majú správcovia overenie nastavené.'}
                        {' '}Faktúry, kampane a správa komentovania vždy vyžadujú zapnuté dvojstupňové
                        overenie a overenú reláciu, aj keď je vynucovanie na serveri vypnuté.
                    </Hint>
                </>
            )}
        </Container>
    );
};

export default Security;
